import { useEffect, useState } from "react";

import { InputField } from "@bokio/designsystem/components/Form/InputField";
import { parseNumber, parseString } from "@bokio/mobile-web-shared/core/utils/numberUtils";
import { formatWithDecimalCount } from "@bokio/shared/utils/format";

import type { TypedInputFieldProps } from "@bokio/designsystem/components/Form/InputField";
import type { FormEvent, KeyboardEvent, RefObject } from "react";

export interface FloatFieldProps extends Partial<TypedInputFieldProps<number | undefined>> {
	zeroOnEmpty?: boolean;
	allowMathematicalExpression?: boolean;
	setAmountInvalid?: (isValid: boolean) => void;
	setMathematicalExpression?: (rawValue: string) => void;
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
	innerRef?: RefObject<HTMLInputElement | null>;
}

/**
 * A field which parses and provides floating point numbers.
 *
 * In either case, onChange() will be called with the parsed float value and optionally
 * the raw string currently in the input field.
 */

export const FloatField = function (props: FloatFieldProps) {
	const [value, setValue] = useState<number>();
	const [focused, setFocused] = useState<boolean>(false);
	const [rawValue, setRawValue] = useState<string>(props.value?.toString() ?? "");

	const getFormattedValue = (number: string) => {
		return props.zeroOnEmpty && number === "" ? 0 : parseString(number);
	};

	const getDisplayValue = (number: string): string => {
		if (props.allowMathematicalExpression) {
			const num = parseNumber(number);
			if (isNaN(num)) {
				const formatted = getFormattedValue(number);
				if (typeof formatted === "number" && isNaN(formatted)) {
					return props.zeroOnEmpty ? "0" : "";
				}

				return formatted.toString();
			}
			return number;
		} else {
			return number;
		}
	};

	useEffect(() => {
		// Keep internal state in sync when parent-provided value changes.
		if (props.value !== value) {
			const formattedRawValue = props.value ? formatWithDecimalCount(props.value, 0, 20) : props.zeroOnEmpty ? "0" : "";
			// eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade
			setRawValue(formattedRawValue);
			// eslint-disable-next-line @eslint-react/set-state-in-effect
			setValue(props.value);
		}
	}, [props.value, props.zeroOnEmpty, value]);

	const notify = (val: number | undefined) => {
		if (props.onChange) {
			props.onChange(val);
		}
	};

	const onChange = (value: string) => {
		setRawValue(value);

		if (props.allowMathematicalExpression) {
			const num = getFormattedValue(value);
			props.setMathematicalExpression && props.setMathematicalExpression(value);

			if (typeof num === "string") {
				setValue(0);
				notify(undefined);
				props.setAmountInvalid && props.setAmountInvalid(true);
			} else if (isNaN(num)) {
				setValue(undefined);
				notify(undefined);
				props.setAmountInvalid && props.setAmountInvalid(true);
			} else {
				props.setAmountInvalid && props.setAmountInvalid(false);
				setValue(num);
				notify(num);
			}
		} else {
			const num = props.zeroOnEmpty && value === "" ? 0 : parseNumber(value);
			if (isNaN(num)) {
				setValue(undefined);
				notify(undefined);
			} else {
				setValue(num);
				notify(num);
			}
		}
	};

	const handleFocus = (e: FormEvent<HTMLInputElement>) => {
		if (props.onFocus) {
			props.onFocus(e);
		}
		setFocused(true);
	};

	const handleBlur = (e: FormEvent<HTMLInputElement>) => {
		if (props.onBlur) {
			props.onBlur(e);
		}
		setFocused(false);
	};

	return (
		<InputField
			innerRef={props.innerRef}
			{...props}
			value={focused ? rawValue : getDisplayValue(rawValue)}
			onChange={onChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			onKeyDown={props.onKeyDown}
		/>
	);

	// We want this to run any time the value of the input field is changed:
	//  - When props.value changes.
	//  - When the user types in the underlying InputField.
	//
	// The reason for this is that we want to give the parsed value to the parent component,
	// even when the first value is provided.
	// onChange(rawValue: string) {

	//   if (parentOnChange) {
	//     if (rawValue === null || rawValue === undefined) {
	//       parentOnChange(NaN, rawValue);
	//       return;
	//     }

	//     const numerics = rawValue.trim();

	//     // TODO: could be "static", and as objects with predicate & transform
	//     const formats: [[(s: string) => boolean, (s: string) => string]] = [
	//       [ // 1234.56
	//         s => /^-?[0-9]+(\.[0-9]+)?$/.test(s),
	//         s => s
	//       ],
	//       [ // 1234,56
	//         s => /^-?[0-9]+(,[0-9]+)?$/.test(s),
	//         s => s.replace(',', '.')
	//       ],
	//       [ // 1,234.56 or 1 234.56
	//         s => /^-?([0-9]+,?)+(\.[0-9]+)?$/.test(s) || /^-?([0-9]+\s?)+(\.[0-9]+)?$/.test(s),
	//         s => s.replace(/[,\s]/g, '')
	//       ],
	//       [ // 1.234,56 or 1 234,56
	//         s => /^-?([0-9]+\.?)+(,[0-9]+)?$/.test(s) || /^-?([0-9]+\s?)+(,[0-9]+)?$/.test(s),
	//         s => s.replace(/[\.\s]/g, '').replace(',', '.')
	//       ],
	//     ];

	//     for (var i = 0; i < formats.length; i++) {
	//       if (formats[i][0](numerics)) {
	//         const candidate = formats[i][1](numerics);
	//         if (this.notifyValidValue(candidate, rawValue, parentOnChange)) {
	//           return;
	//         }
	//       }
	//     }

	//     parentOnChange(NaN, rawValue);
	//   }
	// }
};
